<template>
  <div
    v-tooltip="{
      content: rowTooltip,
      placement: 'bottom-end',
    }"
    class="level-row level__wrapper"
    :class="[
      { 'level__wrapper--opened': !disabled && isWatching },
      { 'level__wrapper--enabled': hasAccess },
      { 'level-row--disabled': disabled },
    ]"
  >
    <div class="level-row__icon-wrapper">
      <slot name="icon">
        <span v-if="contentFinished">
          <GreenTickIcon />
        </span>
        <span v-else>
          <GrayTickIcon />
        </span>
      </slot>
    </div>
    <div class="level__details-wrapper">
      <Action
        v-tooltip="{
          content: iconTooltipText,
          placement: 'bottom-center',
        }"
        ofType="trigger"
        content=""
        :class="[
          'level-row__action',
          !hasAccess ? 'action__trigger--disabled' : '',
          `level-row__action--${contentType}`,
        ]"
        :disabled="!hasAccess"
        :isLoading="isLoading"
      >
        <template #icon>
          <QuizIcon
            v-if="contentType === 'quiz'"
            class="level-row__action-icon"
          />
          <IFrameIcon
            v-if="contentType === 'slides'"
            class="level-row__action-icon level-row__action-icon__iframe"
          />
          <LabIcon
            v-if="contentType === 'lab'"
            class="level-row__action-icon"
          />
          <ExerciseIcon
            v-if="contentType === 'exercise'"
            class="level-row__action-icon"
          />
          <VideoIcon
            v-if="contentType === 'video'"
            class="level-row__action-icon"
          />
          <CourseIcon
            v-if="contentType === 'course'"
            class="level-row__action-icon level-row__action-icon__course"
          />
          <LinkIcon
            v-if="contentType === 'link'"
            class="level-row__action-icon level-row__action-icon__link"
          />
          <LearningPathIcon
            v-if="contentType === 'learning-path'"
            class="level-row__action-icon level-row__action-icon__learning-path"
          />
          <div class="level-row__progress-wrapper">
            <div
              class="level-row__progress-bar"
              :style="`width: ${progress}%`"
            ></div>
          </div>
        </template>
      </Action>
      <LevelTitle
        v-bind="{
          title,
          details,
          labType,
          textClass,
          hasAccess,
          learningArea,
        }"
      />
      <div
        v-if="
          (contentType === 'video' || contentType === 'lesson') &&
          bookmarks.length
        "
        class="level-row__note"
      >
        <NoteIndicator class="level-row__note--icon" />
        <span v-if="bookmarks.length > 1" class="level-row__note--number">
          {{ bookmarks.length }}
        </span>
      </div>
      <LevelControl
        v-if="!hideAction"
        :isLoading="isLoading"
        :hasAccess="hasAccess"
        :isContentActive="isWatching"
        :statusText="statusText"
        :status="status"
        :plansLink="plansLink"
        :parentStatus="parentStatus"
        :contentType="contentType"
      />
    </div>
  </div>
</template>

<script>
import { LinkIcon } from 'vue-feather-icons'
import { mapGetters } from 'vuex'
import GreenTickIcon from '@/assets/icons/course/green_tick.svg'
import GrayTickIcon from '@/assets/icons/course/gray_tick.svg'
import QuizIcon from '@/assets/icons/course/quiz-new-icon.svg'
import IFrameIcon from '@/assets/icons/course/iframe/slides-new-icon.svg'
import ExerciseIcon from '@/assets/icons/course/exercise/exercise-new-icon.svg'
import LabIcon from '@/assets/icons/course/lab/lab_icon.svg'
import VideoIcon from '@/assets/icons/course/video-new-icon.svg'
import CourseIcon from '@/assets/icons/course/course_icon.svg'
import LearningPathIcon from '@/assets/icons/course/learning-path-icon.svg'
import CONSTANTS from '@/constants'
import Action from '@/components/shared/Action.vue'
import NoteIndicator from '@/assets/icons/course/note_indicator.svg'
import LevelTitle from './LevelTitle.vue'
import LevelControl from './LevelControl.vue'

export default {
  name: 'LevelRow',
  components: {
    LevelTitle,
    LevelControl,
    Action,
    GreenTickIcon,
    GrayTickIcon,
    QuizIcon,
    IFrameIcon,
    LabIcon,
    ExerciseIcon,
    VideoIcon,
    CourseIcon,
    LinkIcon,
    NoteIndicator,
    LearningPathIcon,
  },
  props: {
    parentStatus: {
      type: String,
      required: true,
    },
    hasAccess: {
      type: Boolean,
      required: true,
    },
    status: {
      type: String,
      required: false,
      default: '',
    },
    title: {
      type: String,
      required: true,
    },
    rowTooltip: {
      type: String,
      required: false,
      default: '',
    },
    disabled: {
      type: Boolean,
      required: false,
    },
    plansLink: {
      type: String,
      required: true,
    },
    contentType: {
      type: String,
      required: true,
    },
    contentId: {
      type: String,
      required: true,
    },
    statusText: {
      type: String,
      required: false,
      default: '',
    },
    isWatching: {
      type: Boolean,
      required: false,
      default: false,
    },
    details: {
      type: Array,
      required: false,
      default: () => [],
    },
    progress: {
      type: Number,
      required: false,
      default: 0,
    },
    labType: {
      type: String,
      required: false,
      default: '',
    },
    textClass: {
      type: String,
      required: false,
      default: '',
    },
    learningArea: {
      type: String,
      required: false,
      default: '',
    },
    hideAction: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    exercises: {
      type: Array,
      required: false,
      default: () => [],
    },
    isTrackable: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },
  data() {
    return {
      isLoading: false,
    }
  },
  computed: {
    ...mapGetters({
      isMarkAsCompletedEnabled: 'auth/isMarkAsCompletedEnabled',
      allVideosWithBookmarks: 'content/allVideosWithBookmarks',
      currentCourse: 'courses/currentCourse',
    }),
    labIcon() {
      if (this.contentFinished) {
        return GreenTickIcon
      }
      return GrayTickIcon
    },
    iconClass() {
      if (this.ofType === 'custom') return ''
      return `action__${this.ofType}--icon`
    },
    contentFinished() {
      const { FINISHED, VISITED, STARTED } = CONSTANTS.CONTENT_USER_STATUS
      if ([VISITED, FINISHED].includes(this.status)) return true
      const { SLIDES, LAB, EXERCISE } = CONSTANTS.CONTENT_TYPES
      switch (this.contentType) {
        case SLIDES:
          return this.isMarkAsCompletedEnabled ? STARTED === this.status : false
        case LAB: {
          if (FINISHED === this.status) return true
          if (!this.isMarkAsCompletedEnabled) return false
          if (!this.isTrackable) return STARTED === this.status
          return false
        }
        case EXERCISE:
          if (!this.isMarkAsCompletedEnabled) return false
          if (
            this.contentType === CONSTANTS.CONTENT_TYPES.EXERCISE &&
            this.exercises.length > 0
          ) {
            const exercisesStatus =
              this.exercises.map(exercise => exercise.user_status) || []

            return exercisesStatus.every(status =>
              [
                CONSTANTS.CONTENT_USER_STATUS.FINISHED,
                CONSTANTS.CONTENT_USER_STATUS.STARTED,
              ].includes(status)
            )
          }
          break
        default:
          return false
      }
      return false
    },
    iconTooltipText() {
      const { EXERCISE } = CONSTANTS.CONTENT_TYPES
      let textToDisplay = ''
      switch (this.contentType) {
        case EXERCISE:
          if (!this.isMarkAsCompletedEnabled) return `Open ${this.contentType}`
          textToDisplay = this.exercisesActivityState()
          break
        default:
          textToDisplay = `Open ${this.contentType}`
          break
      }
      return textToDisplay
    },
    bookmarks() {
      const bookmarks =
        this.allVideosWithBookmarks[this.contentId]?.bookmarks ?? []
      if (bookmarks.length) {
        return bookmarks.filter(
          ({ metadata }) => metadata?.course_id === this.currentCourse.id
        )
      }
      return bookmarks
    },
  },
  methods: {
    async emitToggle() {
      this.$emit('toggle')
    },
    setLoader(state) {
      this.isLoading = state
    },
    exercisesActivityState() {
      const { EXERCISE } = CONSTANTS.CONTENT_TYPES
      if (this.contentType === EXERCISE && this.exercises.length > 0) {
        const statusCounts = {}
        this.exercises.forEach(e => {
          if (statusCounts[e.user_status]) {
            statusCounts[e.user_status]++
          } else {
            statusCounts[e.user_status] = 1
          }
        })
        const totalExercises = this.exercises.length
        const finishedCount =
          statusCounts[CONSTANTS.CONTENT_USER_STATUS.FINISHED] || 0
        const startedCount =
          statusCounts[CONSTANTS.CONTENT_USER_STATUS.STARTED] || 0

        if (
          finishedCount === totalExercises ||
          startedCount === totalExercises ||
          finishedCount + startedCount === totalExercises
        ) {
          return 'All started'
        } else if (finishedCount > 0 || startedCount > 0) {
          return 'Some started'
        } else {
          return 'None started'
        }
      }
      return false
    },
  },
}
</script>
<style lang="scss" scoped>
.level-row {
  &--disabled {
    opacity: 0.6;
  }
  &__progress-wrapper {
    position: absolute;
    bottom: 0px;
    width: 100%;
    text-align: center;
    border-radius: 5px;
    background: var(--glass-light-gray);
  }
  &__progress-bar {
    background-color: var(--white);
    border-radius: 0px 0px 5px 5px;
    height: 3px;
    position: relative;
  }
  &__icon-wrapper {
    display: flex;
    width: 24px;
    line-height: 0;
    align-self: flex-start;
    margin-top: 5px;
    @include tablet {
      margin: 0 10px 0px 0;
      align-self: center;
    }

    .tickIcon {
      stroke: var(--emperor-gray);
      stroke-width: 3;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }

  &__action-icon {
    min-width: 20px;
    min-height: 18px;
    height: -webkit-fill-available;

    &__link {
      color: var(--light-blue-gray);
    }
  }

  &__action {
    margin-right: auto;
    @include tablet {
      margin-right: 0;
    }
    &--course {
      padding: 0;
    }
  }

  &__note {
    position: relative;
    order: 2;
    line-height: 0;
    @include tablet {
      order: unset;
    }
    &--icon {
      fill: var(--yellow);
      flex: 0 0 auto;
    }
    &--number {
      background: #5b4b9b;
      color: var(--white);
      width: 16px;
      height: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 9px;
      border-radius: 50%;
      position: absolute;
      top: 0;
      right: 0;
      transform: translate(8px, -6px);
    }
  }
}

.action {
  &__trigger {
    background: var(--glass-mid-gray);
  }
}
</style>
